import { useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../features/AppState";
import {
  selectUserData,
  selectUserToken,
} from "../../../../features/userSlice";
import {
  setFetcherSettingsDetailsData,
  setIsDetailsLoading,
} from "../../../../features/fetcherSettingsSlice";
import { FetcherSettingsEndPointLogic } from "../../../../logic/DataCollection/FetcherSettings/FetcherSettingsEndPointLogic";
import InterfaceObjectsPanel from "./InterfaceObjectsPanel";
import ViewInterfaceSkeleton from "./ViewInterfaceSkeleton";
import CountPill from "../CountPill";
import {
  rowId,
  selectCatalogueRows,
  selectDetailObjects,
} from "../helpers/fetcherCatalogue";
import {
  FETCHER_SETTINGS_DETAILS_QUERY_ID,
  getFirstWebsiteSharedUid,
} from "../helpers/fetcherSettingsQueryIds";
import { formatDisplayDateTime } from "../helpers/formatters";

interface Props {
  open: boolean;
  interfaceId: string;
  onClose: () => void;
  onSwitchToEdit: () => void;
  onDuplicate: (interfaceId: string) => void;
  onShowBrands: (interfaceId: string) => void;
  onOpenReport: (reportId: string) => void;
  canEdit: boolean;
}

const SECTION_LABEL_SX = {
  fontSize: "11px",
  letterSpacing: "0.5px",
  textTransform: "uppercase" as const,
  color: "#00000099",
  fontWeight: 700,
};

/**
 * The read-only interface detail. Identity, the strip and the child-report list
 * come off the catalogue response; the objects panel comes from the per-item
 * detail query.
 */
const ViewInterfaceModal = ({
  open,
  interfaceId,
  onClose,
  onSwitchToEdit,
  onDuplicate,
  onShowBrands,
  onOpenReport,
  canEdit,
}: Props) => {
  const dispatch = useDispatch();
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const userData = useSelector(selectUserData);
  const websiteSharedUid = getFirstWebsiteSharedUid(userData);
  const {
    fetcherSettingsMainTablesData,
    fetcherSettingsDetailsData,
    isDetailsLoading,
  } = useSelector((state: AppState) => state.fetcherSettings);
  const backendInterface = useMemo(
    () =>
      selectCatalogueRows(fetcherSettingsMainTablesData, "interface").find(
        (row) => rowId(row.item_id) === interfaceId,
      ),
    [fetcherSettingsMainTablesData, interfaceId],
  );

  useEffect(() => {
    if (!open || !interfaceId || !oauthTokenHeader || !websiteSharedUid) return;
    const loadDetails = async () => {
      try {
        dispatch(setIsDetailsLoading(true));
        dispatch(setFetcherSettingsDetailsData(null));
        await FetcherSettingsEndPointLogic.getFetcherSettingsDetailsData({
          oauthTokenHeader,
          queryId: FETCHER_SETTINGS_DETAILS_QUERY_ID,
          websiteSharedUids: [websiteSharedUid],
          item_id: interfaceId,
          level: "interface",
        });
      } catch (e: any) {
        console.error("Error loading interface details:", e);
      } finally {
        dispatch(setIsDetailsLoading(false));
      }
    };
    loadDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, interfaceId]);

  const childReportRows = useMemo(
    () =>
      selectCatalogueRows(fetcherSettingsMainTablesData, "report")
        .filter((row) => rowId(row.interface_id) === interfaceId)
        .map((row) => ({
          id: rowId(row.item_id),
          name: row.item_name ?? "",
          collectionType: row.collection_type ?? "",
          configs: row.child_configs ?? 0,
          objects: row.own_objects ?? 0,
          brands: row.used_by_brands ?? 0,
        })),
    [fetcherSettingsMainTablesData, interfaceId],
  );

  // an interface is the outermost level, so every object row is its own
  const ownObjects = useMemo(
    () => selectDetailObjects(fetcherSettingsDetailsData, "own"),
    [fetcherSettingsDetailsData],
  );

  if (!backendInterface) return null;

  const usedByBrands = backendInterface.used_by_brands ?? 0;
  const childReports = backendInterface.child_reports ?? 0;
  const childConfigs = backendInterface.child_configs ?? 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: "1000px", maxWidth: "96vw" } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>View Interface</DialogTitle>
      <Typography variant="body2" sx={{ px: 3, pb: 1.5, color: "#00000099" }}>
        {backendInterface.item_name} ({rowId(backendInterface.item_id)}){" "}
        &middot; modified {formatDisplayDateTime(backendInterface.modified)}
      </Typography>
      <Divider />

      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{
          px: 3,
          py: 1.5,
          backgroundColor: "#FAFAFC",
          borderBottom: "1px solid #0000001F",
        }}
      >
        <Stack direction="row" alignItems="baseline" spacing={0.75}>
          <Typography sx={{ fontSize: "18px", fontWeight: 500 }}>
            {usedByBrands}
          </Typography>
          {usedByBrands > 0 ? (
            <Link
              component="button"
              underline="always"
              color="secondary"
              sx={{ fontSize: "12px" }}
              onClick={() => onShowBrands(rowId(backendInterface.item_id))}
            >
              {usedByBrands === 1 ? "brand" : "brands"} - view list
            </Link>
          ) : (
            <Typography sx={{ fontSize: "12px", color: "#00000099" }}>
              brands
            </Typography>
          )}
        </Stack>
        <Divider orientation="vertical" flexItem />
        <Stack direction="row" alignItems="baseline" spacing={0.75}>
          <Typography sx={{ fontSize: "18px", fontWeight: 500 }}>
            {childReports}
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "#00000099" }}>
            reports
          </Typography>
        </Stack>
        <Divider orientation="vertical" flexItem />
        <Stack direction="row" alignItems="baseline" spacing={0.75}>
          <Typography sx={{ fontSize: "18px", fontWeight: 500 }}>
            {childConfigs}
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "#00000099" }}>
            configs
          </Typography>
        </Stack>
      </Stack>

      <DialogContent sx={{ px: 3, py: 2 }}>
        {isDetailsLoading ? (
          <ViewInterfaceSkeleton />
        ) : (
          <>
            <Stack spacing={2.5}>
              <Typography sx={SECTION_LABEL_SX}>Identity</Typography>

              <TextField
                fullWidth
                required
                size="small"
                label="Interface Name"
                value={backendInterface.item_name ?? ""}
                disabled
                color="secondary"
              />

              <TextField
                fullWidth
                size="small"
                label="Common Dynamic Var"
                placeholder="optional"
                value={backendInterface.common_dynamic_var ?? ""}
                disabled
                color="secondary"
              />
            </Stack>

            <InterfaceObjectsPanel
              ownLabel="This interface"
              ownRows={ownObjects}
            />

            <Paper variant="outlined" sx={{ mt: 2 }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  px: 2,
                  py: 1.5,
                  borderBottom: "1px solid #0000001F",
                }}
              >
                Reports on this interface
              </Typography>

              {childReportRows.length === 0 ? (
                <Typography
                  sx={{
                    fontSize: "13px",
                    color: "#00000099",
                    textAlign: "center",
                    py: 3,
                  }}
                >
                  No reports. Nothing can be collected from this interface.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Report</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Configs</TableCell>
                      <TableCell>Objects</TableCell>
                      <TableCell>Brands</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {childReportRows.map((report) => (
                      <TableRow key={report.id}>
                        <TableCell>
                          <Link
                            component="button"
                            underline="always"
                            color="secondary"
                            onClick={() => onOpenReport(report.id)}
                          >
                            {report.name}
                          </Link>{" "}
                          ({report.id})
                        </TableCell>
                        <TableCell>{report.collectionType}</TableCell>
                        <TableCell>
                          <CountPill value={report.configs} />
                        </TableCell>
                        <TableCell>
                          <CountPill value={report.objects} variant="objects" />
                        </TableCell>
                        <TableCell>
                          <CountPill value={report.brands} />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            onClick={() => onOpenReport(report.id)}
                            disabled
                          >
                            Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Paper>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Chip size="small" label="Read only" />
        <Box sx={{ flex: 1 }} />
        <Button color="secondary" onClick={onClose}>
          Close
        </Button>
        {canEdit && (
          <>
            <Button
              color="secondary"
              onClick={() => onDuplicate(rowId(backendInterface.item_id))}
              disabled
            >
              Duplicate
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={onSwitchToEdit}
              disabled
            >
              Edit
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ViewInterfaceModal;
